import { useBeforeLeave } from "@solidjs/router"
import { createSignal, type JSX, onCleanup } from "solid-js"
import { z } from "zod"

const POLL_INTERVAL = 10_000
const BUILD_COMMIT_SHA = import.meta.env.WORKERS_CI_COMMIT_SHA as
  | string
  | undefined

const versionSchema = z.object({ commitSha: z.string() })

export function VersionProvider(props: { children: JSX.Element }) {
  const [needsReload, setNeedsReload] = createSignal(false)

  if (BUILD_COMMIT_SHA) {
    const interval = setInterval(async () => {
      try {
        const resp = await fetch("/version.json", {
          cache: "no-store",
          headers: { "cache-control": "no-cache" },
        })
        if (!resp.ok) return
        const { commitSha } = versionSchema.parse(await resp.json())
        if (commitSha !== BUILD_COMMIT_SHA) {
          setNeedsReload(true)
          clearInterval(interval)
        }
      } catch {
        // Ignore fetch errors (offline, etc.)
      }
    }, POLL_INTERVAL)

    onCleanup(() => clearInterval(interval))
  }

  const onError = () => setNeedsReload(true)
  window.addEventListener("error", onError)
  window.addEventListener("unhandledrejection", onError)
  onCleanup(() => {
    window.removeEventListener("error", onError)
    window.removeEventListener("unhandledrejection", onError)
  })

  useBeforeLeave(e => {
    if (needsReload() && typeof e.to === "string") {
      e.preventDefault()
      window.location.href = e.to
    }
  })

  return <>{props.children}</>
}
